import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  FormControl,
  ClickAwayListener,
  CircularProgress,
  Radio,
  RadioGroup,
  TableContainer,
} from '@mui/material';
// Workaround for MUI icons - import individually
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/Person';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import BackButton from '../../components/BackButton/BackButton';
import { QuoteProductsTable } from '../../components/DataTable/examples';
import styles from './QuoteFormPage.module.css';
import {
  Customer,
  Address,
  Quote,
  QuoteItem,
  Product,
  QuoteItemCreate,
  Contact,
} from '../../types';
import { QuoteProductItem } from '../../components/DataTable/examples/QuoteProductsTable';
import axios from 'axios';
import { createQuote } from '../../services/quote-service';
import {
  getCustomerWithAddresses,
  getCustomers,
  getCustomerWithAddressesAndContacts,
} from '../../services/customer-service';
import CreditCheck from '../../components/CreditCheck';
import ProductDetailsModal from '../../components/ProductDetailsModal/ProductDetailsModal';
import API_CONFIG from '../../config/api';
import { calculatePrice } from '../../services/pricing-service';

// Add these interfaces to match the API response:
interface ProductImage {
  image_url: string;
  alt_text?: string;
  is_primary: boolean;
  display_order: number;
  id: string;
  product_id: string;
}

// Extend the Product interface from imported types
interface ProductWithImages extends Product {
  images?: ProductImage[];
  description?: string;
}

// Product Card Component for Search Results
interface ProductCardProps {
  product: ProductWithImages;
  onAddToQuote: (product: ProductWithImages, quantity: number) => void;
  quoteItems: QuoteItem[];
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToQuote, quoteItems }) => {
  // Check if product exists in quoteItems and get its quantity
  const existingItem = quoteItems.find(item => item.product_id === product.id);
  const [quantity, setQuantity] = useState<number>(existingItem ? existingItem.quantity : 0);
  const [compareSelected, setCompareSelected] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Update local quantity when quoteItems changes
  useEffect(() => {
    const item = quoteItems.find(item => item.product_id === product.id);
    if (item) {
      setQuantity(item.quantity);
    }
  }, [quoteItems, product.id]);

  const handleIncrement = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    // Directly add to quote when incrementing
    onAddToQuote(product, newQuantity);
  };

  const handleDecrement = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      // Update quote with new quantity (or remove if zero)
      onAddToQuote(product, newQuantity);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          border: '1px solid #d9d9d9',
          borderRadius: 2,
          p: 2,
          mb: 2,
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Comparison checkbox - temporarily disabled
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={compareSelected}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCompareSelected(e.target.checked)
                    }
                    className={styles.compareCheckbox}
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    align="center"
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    Comparar
                    <br />
                    produto
                  </Typography>
                }
                sx={{ flexDirection: 'column', m: 0 }}
              />
            </Box>
            */}

            <Box
              sx={{
                width: 96,
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #d9d9d9',
                bgcolor: 'white',
                p: 1,
              }}
            >
              {product.images && product.images.length > 0 ? (
                <img
                  src={
                    product.images.find((img: ProductImage) => img.is_primary)?.image_url ||
                    product.images[0].image_url
                  }
                  alt={product.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    bgcolor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Imagem
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="caption" color="text.secondary">
                SKU: {product.sku}
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight="medium"
                color="#1e1e1e"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                }}
              >
                {product.name}
              </Typography>
              <Typography
                variant="caption"
                color="#007aff"
                sx={{ cursor: 'pointer' }}
                onClick={openModal}
              >
                Ver detalhes do produto
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', md: 'row' },
              alignItems: 'center',
              gap: 2,
              mt: { xs: 2, md: 0 },
              justifyContent: { xs: 'flex-end', md: 'flex-end' },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Typography variant="h6" fontWeight="bold" color="#1e1e1e">
                R${' '}
                {product.price.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
              <Box
                sx={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 0.5,
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography variant="caption">Incluso: IPI / ICMS / ISS / Frete</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={handleDecrement}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid #d9d9d9',
                  borderRadius: 1,
                  color: '#444444',
                }}
              >
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ width: 32, textAlign: 'center' }}>{quantity}</Typography>
              <IconButton
                onClick={handleIncrement}
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid #80c343',
                  borderRadius: 1,
                  color: '#80c343',
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Product Details Modal */}
      <ProductDetailsModal
        open={isModalOpen}
        onClose={closeModal}
        product={product}
        onAddToCart={(product, quantity) => onAddToQuote(product as ProductWithImages, quantity)}
      />
    </>
  );
};

// Product Search Results Component
interface ProductSearchResultsProps {
  searchResults: ProductWithImages[];
  searchTerm: string;
  onAddToQuote: (product: ProductWithImages, quantity: number) => void;
  quoteItems: QuoteItem[];
  isLoading?: boolean;
  onLoadMore?: () => void;
}

const ProductSearchResults: React.FC<ProductSearchResultsProps> = ({
  searchResults,
  searchTerm,
  onAddToQuote,
  quoteItems,
  isLoading = false,
  onLoadMore,
}) => {
  return (
    <Box
      sx={{
        bgcolor: 'white',
        p: 2,
        borderRadius: 1,
        border: '1px solid #d9d9d9',
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      {searchTerm && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {searchResults.length > 0
            ? `Resultados para "${searchTerm}"`
            : isLoading
              ? `Pesquisando por "${searchTerm}"...`
              : `Nenhum produto encontrado para "${searchTerm}"`}
        </Typography>
      )}

      {/* Product results */}
      {searchResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {searchResults.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToQuote={onAddToQuote}
              quoteItems={quoteItems}
            />
          ))}
        </Box>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} sx={{ color: 'var(--acer-green)' }} />
        </Box>
      )}

      {/* Load more button */}
      {searchResults.length > 0 && onLoadMore && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={onLoadMore}
            variant="outlined"
            sx={{ borderColor: 'var(--acer-green)', color: 'var(--acer-green)' }}
            disabled={isLoading}
          >
            Carregar mais produtos
          </Button>
        </Box>
      )}
    </Box>
  );
};

const QuoteFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [primaryContact, setPrimaryContact] = useState<Contact | null>(null);
  const [paymentOption, setPaymentOption] = useState<'Partial Credit' | 'Bank Slip'>('Bank Slip');
  const [paymentTerm, setPaymentTerm] = useState<'30' | '60' | '90' | '120' | '30/60' | '30/60/90'>(
    '30'
  );
  const [sameAddressForShipping, setSameAddressForShipping] = useState(true);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<Address | null>(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<Address | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ProductWithImages[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 4,
    total: 0,
  });
  const [requestCredit, setRequestCredit] = useState(false);
  const [creditEmail, setCreditEmail] = useState('');
  const [creditCnpj, setCreditCnpj] = useState('');
  const [creditWhatsapp, setCreditWhatsapp] = useState('');
  const [quote, setQuote] = useState<any>(null); // Replace with proper type
  const [observation, setObservation] = useState<string>('');
  const [quoteItems, setQuoteItems] = useState<QuoteItem[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  // Add a state to track if the search container is currently being interacted with
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [customerUf, setCustomerUf] = useState<string | null>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumb();
  // Get the current user from Redux state
  const { user } = useAppSelector(state => state.user);

  // Function to fetch customers from API
  const getCustomersFromAPI = async () => {
    setLoading(true);
    try {
      const response = await getCustomers();
      if (response.success) {
        setCustomers(response.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      // You could add error state handling here
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch customer with addresses from API
  const fetchCustomerWithAddresses = async (customerId: string) => {
    console.log('Fetching addresses for customer:', customerId); // Debug log
    try {
      const response = await getCustomerWithAddressesAndContacts(customerId);

      if (response.success && response.data) {
        console.log(`Found ${response.data.addresses.length} addresses for customer`);
        console.log(`Found ${response.data.contacts?.length || 0} contacts for customer`);

        // Prefer the billing address because pricing is calculated from the customer's billing UF.
        const defaultBillingAddress =
          response.data.addresses.find((address: Address) => address.is_billing === true) ||
          response.data.addresses.find((address: Address) => address.is_default === true) ||
          response.data.addresses[0];

        console.log('Setting billing address:', defaultBillingAddress);
        // Set billing address
        setSelectedBillingAddress(defaultBillingAddress);

        // Also set shipping address if same as billing
        if (sameAddressForShipping) {
          console.log('Using same address for shipping');
          setSelectedShippingAddress(defaultBillingAddress);
        }

        // Find and set primary contact
        if (response.data.contacts && response.data.contacts.length > 0) {
          const primary = response.data.contacts.find(
            (contact: Contact) => contact.is_primary === true
          );
          setPrimaryContact(primary || response.data.contacts[0]);
        } else {
          setPrimaryContact(null);
        }
      } else {
        console.log('No addresses found for customer or invalid response format');
        // Reset addresses if none found
        setSelectedBillingAddress(null);
        setSelectedShippingAddress(null);
        setPrimaryContact(null);
      }
    } catch (error) {
      console.error('Error fetching customer addresses:', error);
      // Reset addresses on error
      setSelectedBillingAddress(null);
      setSelectedShippingAddress(null);
      setPrimaryContact(null);
    }
  };

  // Function to handle customer selection and trigger price recalculation
  const handleCustomerChange = async (customerId: string) => {
    console.log('Customer select onChange triggered');
    console.log('Selected customer ID:', customerId);

    if (!customerId) {
      console.log('No customer ID selected, skipping');
      // Reset customer UF information when customer is deselected
      setCustomerUf(null);
      // Clear cart when customer is deselected
      setQuoteItems([]);
      // Clear search results and search term
      setSearchResults([]);
      setSearchTerm('');
      setShowSearchResults(false);
      return;
    }

    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      console.log('Found customer:', customer.company_name);

      // Reset cart when customer changes (if there was a previous customer)
      if (selectedCustomer && selectedCustomer.id !== customerId) {
        console.log('Customer changed, resetting cart');
        setQuoteItems([]);
        // Clear search results and search term
        setSearchResults([]);
        setSearchTerm('');
        setShowSearchResults(false);
      }

      setSelectedCustomer(customer);

      // Reset customer UF information when customer changes
      setCustomerUf(null);

      // Fetch customer with addresses after selection
      console.log('Calling fetchCustomerWithAddresses with ID:', customerId);
      await fetchCustomerWithAddresses(customerId);

      // When addresses are loaded, recalculation will be triggered by the useEffect below
    } else {
      console.log('Customer not found in customers array');
    }
  };

  const mockCustomer: Customer = {
    id: '1',
    company_name: 'Tech Solutions Ltda',
    phone: '(00) 0000-0000',
    email: 'contato@email.com.br',
    cnpj: '00.000.000/0001-00',
    sector: 'Corporativo',
    created_at: '2025-02-27T10:00:00Z',
  };

  // Fetch products from API
  const fetchProducts = async (search: string, skip = 0, limit = 5) => {
    try {
      setIsLoading(true);
      // Get token from your auth system
      const token = localStorage.getItem('token'); // Adjust based on your auth implementation

      let url: string;
      const params: any = {
        search: encodeURIComponent(search),
        skip,
        limit,
        payment_term: paymentTerm,
      };

      // Use different endpoint based on whether customer is selected
      if (selectedCustomer) {
        // Use the for-quote endpoint with customer-specific pricing
        url = `${API_CONFIG.BASE_URL_WITH_VERSION}/products/for-quote`;
        params.customer_id = selectedCustomer.id;
        if (selectedBillingAddress?.id) {
          params.address_id = selectedBillingAddress.id;
        }
      } else {
        // Use the regular products endpoint
        url = `${API_CONFIG.BASE_URL_WITH_VERSION}/products/`;
        // When no customer is selected, we'll rely on the pricing service to calculate SP prices
      }

      // Build query string
      const queryString = new URLSearchParams(params).toString();
      console.log(`Fetching products from: ${url}?${queryString}`);

      const response = await axios.get(`${url}?${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Simple log of API response (no sensitive data)
      console.log('Products API response received');

      // The API returns an array directly, not an object with items and total
      if (Array.isArray(response.data)) {
        // For the for-quote endpoint, prices are already calculated based on customer UF
        // For the regular endpoint, we need to calculate SP prices using the pricing service
        const mappedProducts = await Promise.all(
          response.data.map(async (product: any) => {
            // If using the for-quote endpoint, prices are already correct
            if (selectedCustomer) {
              return product;
            }

            // For regular endpoint (no customer), just return product as is
            // The regular endpoint should already have SP prices calculated

            // Fallback: if pricing service fails, use original price or calculated_price
            if (product.calculated_price !== undefined && product.calculated_price !== null) {
              return {
                ...product,
                price: product.calculated_price,
              };
            }

            return product;
          })
        );

        setSearchResults(mappedProducts);
        setPagination({
          skip,
          limit,
          total: mappedProducts.length,
        });
      } else if (response.data && response.data.items) {
        // Fallback for possible different response format - apply same logic
        const mappedProducts = await Promise.all(
          response.data.items.map(async (product: any) => {
            // If using the for-quote endpoint, prices are already correct
            if (selectedCustomer) {
              return product;
            }

            // For regular endpoint (no customer), just return product as is
            // The regular endpoint should already have SP prices calculated

            if (product.calculated_price !== undefined && product.calculated_price !== null) {
              return {
                ...product,
                price: product.calculated_price,
              };
            }

            return product;
          })
        );

        setSearchResults(mappedProducts);
        setPagination({
          skip,
          limit,
          total: response.data.total || 0,
        });
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch products when search term changes
  useEffect(() => {
    if (searchTerm.length > 0 && selectedCustomer) {
      // Show search results as soon as user types (don't wait for API)
      setShowSearchResults(true);

      // Add debounce to avoid too many API calls
      const handler = setTimeout(() => {
        fetchProducts(searchTerm, pagination.skip, pagination.limit);
      }, 300);

      return () => {
        clearTimeout(handler);
      };
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [
    searchTerm,
    pagination.skip,
    pagination.limit,
    selectedCustomer,
    selectedBillingAddress?.id,
    paymentTerm,
  ]);

  // Effect to refetch products when customer changes (to recalculate prices based on customer UF)
  useEffect(() => {
    if (searchTerm.length > 0) {
      // Always refetch products when customer changes (including when deselected)
      fetchProducts(searchTerm, pagination.skip, pagination.limit);
    }
  }, [selectedCustomer?.id, selectedBillingAddress?.id, paymentTerm]); // Trigger on customer, address and payment term changes

  // Handle adding product to quote
  const handleAddToQuote = async (product: ProductWithImages, quantity: number) => {
    // Check if product already exists in quote items
    const existingItemIndex = quoteItems.findIndex(item => item.product_id === product.id);

    if (quantity === 0) {
      // If quantity is 0, remove the item from the cart
      if (existingItemIndex >= 0) {
        setQuoteItems(prevItems => prevItems.filter(item => item.product_id !== product.id));
      }
      // No need to do anything if the item doesn't exist in the cart
      return;
    }

    // If product exists, update its quantity (and potentially recalculate price if customer changed)
    if (existingItemIndex >= 0) {
      // For existing items, we might want to recalculate price if customer has changed
      // But for now, just update quantity - price recalculation happens in useEffect
      setQuoteItems(prevItems =>
        prevItems.map((item, index) =>
          index === existingItemIndex ? { ...item, quantity: quantity } : item
        )
      );
      return;
    }

    // Use the product price from search results (which should already be correctly calculated)
    // The search results should already have the correct customer-specific pricing
    const finalPrice = product.price;

    // Create a new quote item with the price from search results (or recalculated if needed)
    const newItem: QuoteItem = {
      id: `item-${Date.now()}`, // Generate a unique ID
      quote_id: id || 'new-quote',
      product_id: product.id,
      product: product as unknown as Product, // Cast to Product type
      quantity: quantity,
      unit_price: finalPrice,
    };

    // Add to quote items
    setQuoteItems(prevItems => [...prevItems, newItem]);
  };

  // Handle removing item from quote
  const handleRemoveItem = (itemId: string) => {
    setQuoteItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  // Handle quantity changes
  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    if (newQuantity < 0) return;

    if (newQuantity === 0) {
      // If quantity is set to 0, remove the item from the cart
      setQuoteItems(prevItems => prevItems.filter(item => item.id !== itemId));
      return;
    }

    setQuoteItems(prevItems =>
      prevItems.map(item => (item.id === itemId ? { ...item, quantity: newQuantity } : item))
    );
  };

  useEffect(() => {
    // Fetch quote data if in edit mode
    if (isEditMode) {
      // In a real app, fetch the quote data
      setQuote({ id, title: `Cotação #${id}` }); // Mock data
    }
  }, [id, isEditMode]);

  useEffect(() => {
    // Set breadcrumb based on mode
    if (isEditMode && quote) {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
        { label: 'Cotações', path: '/quotes' },
        { label: quote.title, path: `/quotes/${id}` },
        { label: 'Editar' },
      ]);
    } else {
      setBreadcrumbs([
        { label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> },
        { label: 'Cotações', path: '/quotes' },
        { label: 'Nova Cotação' },
      ]);
    }

    // Fetch customers when component mounts
    getCustomersFromAPI();

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <HomeIcon sx={{ fontSize: 16 }} /> }]);
    };
  }, [isEditMode, quote, id, setBreadcrumbs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Reset pagination when search term changes
    setPagination((prev: typeof pagination) => ({ ...prev, skip: 0 }));
  };

  const handleMouseDown = () => {
    // Set search as active when mouse is pressed down on the search container
    setIsSearchActive(true);
  };

  const handleMouseUp = () => {
    // Handle global mouse up event to detect clicks outside the search container
    if (!searchRef.current?.contains(document.activeElement)) {
      setIsSearchActive(false);
      if (searchTerm.length > 0) {
        setShowSearchResults(false);
      }
    }
  };

  // Add event listener for mouse up events
  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleFocus = () => {
    // Show search results dropdown when field gets focus, but only if there's already text
    if (searchTerm.length > 0) {
      setShowSearchResults(true);
      setIsSearchActive(true);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSaveAsDraft = async () => {
    try {
      if (!selectedCustomer) {
        alert('Por favor, selecione um cliente');
        return;
      }

      if (!selectedShippingAddress) {
        alert('Por favor, selecione um endereço de entrega');
        return;
      }

      // Validate that we have a valid user ID
      if (!user?.id || typeof user.id !== 'string' || user.id.length < 32) {
        alert(
          'Usuário não está autenticado ou ID de usuário inválido. Por favor, faça login novamente.'
        );
        return;
      }

      // Prepare items data with the proper type
      const items: QuoteItemCreate[] = quoteItems.map(({ product_id, quantity, unit_price }) => ({
        product_id,
        quantity,
        unit_price,
        discount_percent: 0,
        discount_amount: 0,
      }));

      // Prepare quote data for draft with items included
      const quoteData: Omit<Quote, 'id' | 'items'> & {
        status: Quote['status'];
        items: QuoteItemCreate[];
      } = {
        customer_id: selectedCustomer.id,
        payment_method: paymentOption as 'Partial Credit' | 'Bank Slip',
        payment_term: paymentTerm,
        shipping_address_id: selectedShippingAddress.id,
        status: 'Draft',
        expiration_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0], // 30 days from now
        observation,
        items: items, // Include the items with the quote
      };

      console.log('Sending quote data:', quoteData);

      // Use the quote service instead of direct axios call
      const response = await createQuote(quoteData);

      console.log('Quote draft saved successfully:', response.data);
      alert('Rascunho salvo com sucesso!');
      navigate('/quotes');
    } catch (error) {
      console.error('Error saving quote draft:', error);
      if (axios.isAxiosError(error)) {
        alert(`Erro ao salvar rascunho: ${error.response?.data?.detail || error.message}`);
      } else {
        alert('Erro ao salvar rascunho. Por favor, tente novamente.');
      }
    }
  };

  const handleGeneratePdf = () => {
    // Generate PDF logic would go here
    alert('Gerando PDF da cotação');
  };

  const handleFinishQuote = async () => {
    try {
      if (!selectedCustomer) {
        alert('Por favor, selecione um cliente');
        return;
      }

      if (!selectedShippingAddress) {
        alert('Por favor, selecione um endereço de entrega');
        return;
      }

      // Prepare quote items
      const items: QuoteItemCreate[] = quoteItems.map(({ product_id, quantity, unit_price }) => ({
        product_id,
        quantity,
        unit_price,
        discount_percent: 0,
        discount_amount: 0,
      }));

      // Prepare quote data for submission
      const quoteData: Omit<Quote, 'id' | 'items'> & {
        status: Quote['status'];
        items: QuoteItemCreate[];
      } = {
        customer_id: selectedCustomer.id,
        payment_method: paymentOption as 'Partial Credit' | 'Bank Slip',
        payment_term: paymentTerm,
        shipping_address_id: selectedShippingAddress.id,
        status: 'Submitted', // Set as Submitted when finishing
        expiration_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0], // 30 days from now
        observation,
        items: items,
      };

      console.log('Sending quote data:', quoteData);

      // Use the quote service to create the quote
      const response = await createQuote(quoteData);

      console.log('Quote created successfully:', response.data);
      alert('Cotação finalizada e enviada para aprovação!');
      navigate('/quotes');
    } catch (error) {
      console.error('Error creating quote:', error);
      if (axios.isAxiosError(error)) {
        alert(`Erro ao criar cotação: ${error.response?.data?.detail || error.message}`);
      } else {
        alert('Erro ao criar cotação. Por favor, tente novamente.');
      }
    }
  };

  // Calculate totals
  const subtotal = quoteItems.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  // No discount calculation, total final is the same as subtotal
  const finalTotal = subtotal;

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Load more products (pagination)
  const handleLoadMore = () => {
    setPagination(prev => ({
      ...prev,
      skip: prev.skip + prev.limit,
    }));
  };

  // Function to recalculate prices when customer or billing address changes
  useEffect(() => {
    // Skip if no customer or address is selected
    if (!selectedCustomer || !selectedBillingAddress) return;

    // Skip if no quote items
    if (quoteItems.length === 0) return;

    // Create a reference to track whether we should proceed with the calculation
    let isActive = true;

    // Set up a reference for the timeout to avoid race conditions
    const timeoutRef = setTimeout(async () => {
      // Only proceed if component is still mounted and we have items
      if (!isActive) return;

      console.log('Recalculating prices for', quoteItems.length, 'items based on customer/address');

      try {
        // Create a deep copy of items to avoid modifying state directly
        const itemsToUpdate = [...quoteItems];
        const updatedItems: QuoteItem[] = [];

        // Process each item sequentially to prevent race conditions
        for (const item of itemsToUpdate) {
          try {
            // Use pricing service to calculate price based on customer ID
            const priceResponse = await calculatePrice({
              product_id: item.product_id,
              customer_id: selectedCustomer.id,
              payment_term: paymentTerm,
              address_id: selectedBillingAddress.id,
            });

            if (priceResponse.success) {
              // Get customer UF from the first calculation if available
              if (updatedItems.length === 0 && priceResponse.data.customer_uf) {
                setCustomerUf(priceResponse.data.customer_uf);
              }

              // Only use the final_price from the backend, no internal pricing details
              updatedItems.push({
                ...item,
                unit_price: priceResponse.data.final_price,
              });
            } else {
              // If calculation failed, keep original price
              updatedItems.push(item);
            }
          } catch (error) {
            console.error(`Failed to calculate price for product ${item.product_id}:`, error);
            // Keep original price on error
            updatedItems.push(item);
          }
        }

        // Only update if component is still mounted
        if (isActive) {
          // Update all quote items with new prices at once
          setQuoteItems(updatedItems);
        }
      } catch (error) {
        console.error('Error recalculating prices:', error);
      }
    }, 300); // Add a 300ms debounce to avoid duplicate calculations

    // Clean up the timeout when the component unmounts or dependencies change
    return () => {
      isActive = false;
      clearTimeout(timeoutRef);
    };

    // Only recalculate when customer or address changes or when
    // quoteItems is first populated (not on every length change)
  }, [selectedCustomer?.id, selectedBillingAddress?.id, paymentTerm]);

  return (
    <>
      {/* Back button */}
      <BackButton sx={{ mb: 2 }} onClick={handleGoBack} />

      {/* Header section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#2b3445', fontWeight: 600 }}>
          Monte sua cotação de forma rápida e flexível!
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Pesquise produtos, ajuste as quantidades e escolha a melhor condição de pagamento para seu
          negócio.
          <br />
          Assim que finalizar, você poderá gerar em PDF ou converter a cotação em pedido.
        </Typography>
      </Box>

      <Box sx={{ padding: '23px 16px 22px 16px', backgroundColor: 'white' }}>
        {/* Quotation Header */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            alignSelf: 'stretch',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                src="/icons/quote.svg"
                alt="Quote icon"
                style={{
                  width: 24,
                  height: 24,
                  color: 'var(--acer-green) !important',
                  marginRight: '12px',
                }}
              />
              <Typography sx={{ fontWeight: 500 }}>
                {isEditMode ? quote?.title : 'Cotação - ACR-2025'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Data de Criação: {new Date().toLocaleDateString('pt-BR')}
            </Typography>
          </Box>
        </Box>

        {/* Client Selection */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={selectedCustomer?.id || ''}
                  displayEmpty
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src="/icons/store.svg"
                        alt="Store icon"
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: '8px',
                        }}
                      />
                      {selected ? selectedCustomer?.company_name : 'Selecione um cliente'}
                    </Box>
                  )}
                  onChange={e => handleCustomerChange(e.target.value as string)}
                  sx={{
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d9d9d9',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    Selecione um cliente
                  </MenuItem>
                  {loading ? (
                    <MenuItem disabled>Carregando clientes...</MenuItem>
                  ) : (
                    customers.map(customer => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="body2">Cliente novo?</Typography>
                <Button
                  component={Link}
                  to="/customers/new"
                  sx={{ color: 'primary.main', textTransform: 'none', p: 0 }}
                >
                  Cadastre aqui
                </Button>
              </Box>
            </Grid>
          </Grid>

          {selectedCustomer && (
            <Box
              sx={{
                bgcolor: '#353535',
                color: 'white',
                p: 2,
                mt: 2,
                borderRadius: '8px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <img
                  src="/icons/store.svg"
                  alt="Store icon"
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: '8px',
                  }}
                />
                <Typography variant="body2">{selectedCustomer.company_name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', color: '#d9d9d9', fontSize: 12, mb: 1 }}>
                <Typography variant="caption">CNPJ: {selectedCustomer.cnpj}</Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 2, bgcolor: '#d9d9d9', opacity: 0.5 }}
                />
                <Typography variant="caption">Setor: {selectedCustomer.sector}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', color: '#d9d9d9', fontSize: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Typography variant="caption">Contato Comercial:</Typography>
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {primaryContact ? primaryContact.name : 'Não informado'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Typography variant="caption">📧</Typography>
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {primaryContact ? primaryContact.email : selectedCustomer.email}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption">📱</Typography>
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {primaryContact ? primaryContact.phone : selectedCustomer.phone}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                  <Typography variant="caption">
                    {primaryContact && primaryContact.role ? primaryContact.role : 'Financeiro'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Divider */}
        <Divider
          sx={{
            my: 2,
            borderColor: '#d9d9d9',
            opacity: 0.7,
          }}
        />

        {/* Add Products */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '4px',
            border: '1px solid #CAC4D0',
            padding: '15px 19px',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/icons/add-shopping-cart.svg"
              alt="Add shopping cart icon"
              style={{
                width: 24,
                height: 24,
                marginRight: '8px',
              }}
            />
            <Typography sx={{ fontWeight: 500 }}>Adicionar produtos</Typography>
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
            Adicione os produtos à sua cotação de forma rápida e inteligente
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {selectedCustomer ? (
              <>
                Use a busca inteligente para encontrar produtos rapidamente.
                <br />
                Digite o nome, código ou categoria e adicione os itens à sua cotação com apenas um
                clique.
              </>
            ) : (
              <>
                Selecione um cliente primeiro para habilitar a busca de produtos.
                <br />A seleção é necessária para calcular preços específicos baseados na
                localização do cliente.
              </>
            )}
          </Typography>

          {/* Search box */}
          <Box sx={{ mb: 3, position: 'relative' }} ref={searchRef} onMouseDown={handleMouseDown}>
            <TextField
              fullWidth
              placeholder={
                selectedCustomer
                  ? 'Buscar por nome do produto, código ou categoria'
                  : 'Selecione um cliente primeiro para buscar produtos'
              }
              value={searchTerm}
              onChange={handleSearch}
              onFocus={handleFocus}
              variant="outlined"
              disabled={!selectedCustomer}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color={selectedCustomer ? 'action' : 'disabled'} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {isLoading && selectedCustomer && (
                      <CircularProgress size={20} sx={{ color: 'var(--acer-green)' }} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            {selectedCustomer && (
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  zIndex: 10,
                  display: showSearchResults && isSearchActive ? 'block' : 'none',
                }}
              >
                <ProductSearchResults
                  searchResults={searchResults}
                  searchTerm={searchTerm}
                  onAddToQuote={handleAddToQuote}
                  quoteItems={quoteItems}
                  isLoading={isLoading}
                  onLoadMore={handleLoadMore}
                />
              </Box>
            )}
          </Box>

          {/* Products Table */}
          {quoteItems.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <img
                  src="/icons/ultimos-pedidos.svg"
                  alt="Ultimos pedidos icon"
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: '8px',
                  }}
                />
                <Typography sx={{ fontWeight: 500 }}>Meus produtos</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                {/* Using the QuoteProductsTable component */}
                <QuoteProductsTable
                  data={quoteItems as unknown as QuoteProductItem[]}
                  onRemoveItem={handleRemoveItem}
                  onQuantityChange={handleQuantityChange}
                />
              </Box>

              {/* Totals */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ mr: 4, fontWeight: 500 }}>Total:</Typography>
                  <Typography sx={{ fontWeight: 700 }}>R$ {formatCurrency(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ mr: 4, fontSize: 14 }}>Subtotal: R$</Typography>
                  <Typography sx={{ fontSize: 14 }}>{formatCurrency(subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ mr: 4, fontSize: 14 }}></Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    Impostos {customerUf ? `(${customerUf})` : ''} incluídos
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography sx={{ mr: 4, fontSize: 14 }}></Typography>
                  <Typography sx={{ color: 'var(--acer-green)', fontSize: 14 }}>
                    Frete Grátis
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ mr: 4, fontWeight: 700 }}>Total Final: R$</Typography>
                  <Typography sx={{ fontWeight: 700 }}>{formatCurrency(finalTotal)}</Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                border: '1px solid #BDBDBD',
                borderRadius: '4px',
                padding: '27px 19px',
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {!selectedCustomer
                  ? 'Selecione um cliente primeiro para adicionar produtos'
                  : 'Nenhum produto adicionado a cotação'}
              </Typography>
              {!selectedCustomer && (
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  A seleção de cliente é necessária para calcular preços específicos e condições de
                  pagamento
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Only show Payment and Address sections if a customer is selected */}
        {selectedCustomer && (
          <>
            {/* Payment */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '1px solid #CAC4D0',
                padding: '15px 19px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/icons/barcode-scanner.svg"
                  alt="Payment icon"
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: '8px',
                  }}
                />
                <Typography sx={{ fontWeight: 500 }}>Pagamento</Typography>
              </Box>

              <RadioGroup
                aria-label="payment-options"
                name="payment-options"
                value={paymentOption}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPaymentOption(e.target.value as 'Partial Credit' | 'Bank Slip')
                }
                defaultValue="Bank Slip"
              >
                <FormControlLabel
                  value="Bank Slip"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: 'var(--acer-green)',
                        },
                      }}
                      defaultChecked
                    />
                  }
                  label="Boleto Bancário"
                  sx={{ mb: 2 }}
                />
                {/*
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Outras opções disponíveis:
                </Typography>
                <FormControlLabel
                  value="Bank Slip"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': {
                          color: 'var(--acer-green)',
                        },
                      }}
                    />
                  }
                  label="À vista no boleto (-10%)"
                  sx={{ mb: 2 }}
                />
                */}
              </RadioGroup>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
                  Prazo
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={paymentTerm === '30' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentTerm('30')}
                    sx={{
                      flex: 1,
                      borderColor: paymentTerm === '30' ? 'var(--acer-green)' : '#d9d9d9',
                      backgroundColor: paymentTerm === '30' ? 'var(--acer-green)' : 'transparent',
                      color: paymentTerm === '30' ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: paymentTerm === '30' ? 'var(--acer-green)' : 'transparent',
                        borderColor: 'var(--acer-green)',
                      },
                    }}
                  >
                    30 dias
                  </Button>
                  <Button
                    variant={paymentTerm === '60' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentTerm('60')}
                    sx={{
                      flex: 1,
                      borderColor: paymentTerm === '60' ? 'var(--acer-green)' : '#d9d9d9',
                      backgroundColor: paymentTerm === '60' ? 'var(--acer-green)' : 'transparent',
                      color: paymentTerm === '60' ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: paymentTerm === '60' ? 'var(--acer-green)' : 'transparent',
                        borderColor: 'var(--acer-green)',
                      },
                    }}
                  >
                    60 dias
                  </Button>
                  {/* <Button
                    variant={paymentTerm === '90' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentTerm('90')}
                    sx={{
                      flex: 1,
                      borderColor: paymentTerm === '90' ? 'var(--acer-green)' : '#d9d9d9',
                      backgroundColor: paymentTerm === '90' ? 'var(--acer-green)' : 'transparent',
                      color: paymentTerm === '90' ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor: paymentTerm === '90' ? 'var(--acer-green)' : 'transparent',
                        borderColor: 'var(--acer-green)',
                      },
                    }}
                  >
                    90 dias
                  </Button> */}
                  <Button
                    variant={paymentTerm === '30/60' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentTerm('30/60')}
                    sx={{
                      flex: 1,
                      borderColor: paymentTerm === '30/60' ? 'var(--acer-green)' : '#d9d9d9',
                      backgroundColor:
                        paymentTerm === '30/60' ? 'var(--acer-green)' : 'transparent',
                      color: paymentTerm === '30/60' ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor:
                          paymentTerm === '30/60' ? 'var(--acer-green)' : 'transparent',
                        borderColor: 'var(--acer-green)',
                      },
                    }}
                  >
                    30/60
                  </Button>
                  <Button
                    variant={paymentTerm === '30/60/90' ? 'contained' : 'outlined'}
                    onClick={() => setPaymentTerm('30/60/90')}
                    sx={{
                      flex: 1,
                      borderColor: paymentTerm === '30/60/90' ? 'var(--acer-green)' : '#d9d9d9',
                      backgroundColor:
                        paymentTerm === '30/60/90' ? 'var(--acer-green)' : 'transparent',
                      color: paymentTerm === '30/60/90' ? 'white' : 'text.primary',
                      '&:hover': {
                        backgroundColor:
                          paymentTerm === '30/60/90' ? 'var(--acer-green)' : 'transparent',
                        borderColor: 'var(--acer-green)',
                      },
                    }}
                  >
                    30/60/90
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  Observação que sairá na Nota Fiscal
                </Typography>
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  placeholder="Observações sobre a cotação/pedido"
                  value={observation}
                  onChange={e => setObservation(e.target.value)}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'var(--acer-green)' }}>
                    Endereço de Faturamento
                  </Typography>
                  <IconButton size="small" sx={{ color: '#505050' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                {selectedBillingAddress && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedCustomer?.company_name}
                    </Typography>
                    <Typography variant="body2">
                      {selectedBillingAddress.street} - {selectedBillingAddress.city} -{' '}
                      {selectedBillingAddress.state} {selectedBillingAddress.zip_code}
                    </Typography>
                  </Box>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameAddressForShipping}
                    onChange={e => setSameAddressForShipping(e.target.checked)}
                    sx={{
                      '&.Mui-checked': {
                        color: 'var(--acer-green)',
                      },
                    }}
                  />
                }
                label="Usar mesmo endereço para entrega"
              />
            </Box>

            {/* Delivery 
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '4px',
                border: '1px solid #CAC4D0',
                padding: '15px 19px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src="/icons/barcode-scanner.svg"
                  alt="Address and delivery icon"
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: '8px',
                  }}
                />
                <Typography sx={{ fontWeight: 500 }}>Endereço e Prazo de Entrega</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sameAddressForShipping}
                    onChange={e => setSameAddressForShipping(e.target.checked)}
                    sx={{
                      '&.Mui-checked': {
                        color: 'var(--acer-green)',
                      },
                    }}
                  />
                }
                label="Usar mesmo endereço para entrega"
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" sx={{ mb: 2 }}>
                Prazo Estimado: 7 a 10 dias úteis após aprovação do pedido.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'var(--acer-green)' }}>
                    Endereço de entrega
                  </Typography>
                  <IconButton size="small" sx={{ color: '#505050' }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                {selectedShippingAddress && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedCustomer?.company_name}
                    </Typography>
                    <Typography variant="body2">
                      {selectedShippingAddress.street} - {selectedShippingAddress.city} -{' '}
                      {selectedShippingAddress.state} {selectedShippingAddress.zip_code}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            */}
          </>
        )}
      </Box>

      {/* Credit Check */}
      {/*
      <CreditCheck
        creditEmail={creditEmail}
        setCreditEmail={setCreditEmail}
        creditCnpj={creditCnpj} 
        setCreditCnpj={setCreditCnpj}
        creditWhatsapp={creditWhatsapp}
        setCreditWhatsapp={setCreditWhatsapp}
        requestCredit={requestCredit}
        setRequestCredit={setRequestCredit}
      />
      */}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        {/* 
        <Button
          variant="outlined"
          sx={{
            color: '#1E1E1E',
            borderColor: '#1E1E1E',
            '&:hover': { borderColor: '#1c1b1f', bgcolor: 'rgba(0,0,0,0.04)' },
            textTransform: 'none',
            borderRadius: '4px',
          }}
          onClick={handleSaveAsDraft}
        >
          Salvar Rascunho
        </Button>
        
        <Button
          variant="outlined"
          sx={{
            color: '#1E1E1E',
            borderColor: '#1E1E1E',
            '&:hover': { borderColor: '#1c1b1f', bgcolor: 'rgba(0,0,0,0.04)' },
            textTransform: 'none',
            borderRadius: '4px',
          }}
          onClick={handleGeneratePdf}
        >
          Gerar PDF da cotação
        </Button>
        */}

        <Button
          variant="contained"
          sx={{
            bgcolor: '#1E1E1E',
            '&:hover': { bgcolor: '#1c1b1f' },
            textTransform: 'none',
            borderRadius: '4px',
            color: 'white',
          }}
          onClick={handleFinishQuote}
        >
          Finalizar Cotação
        </Button>
      </Box>
    </>
  );
};

export default QuoteFormPage;
